import * as SwitchPrimitives from '@radix-ui/react-switch';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * `sm` (16×28) is the size almost every Mora surface wants, INCLUDING settings
 * rows. `default` (24×44) is shadcn stock, sized for a 14–16px body scale.
 *
 * ⚠️ This comment used to say `default` was "the FORM control — settings rows,
 * dialogs, anywhere the switch is the thing you came to touch", and that claim
 * was the defect. Mora's settings row title is `SETTINGS_ROW_TITLE`, which is
 * `text-caption` — 12px. A 24px switch beside a 12px label is 2.0× the height
 * of the thing it labels, so it wins the row every time. Founder, 2026-08-22,
 * reported it independently on three separate pages (Privacy → face detection,
 * Profile → email notifications, Automations → Engagement Hub): "it looks like
 * all the toggles are really, really big." Three independent reports of one
 * symptom is the primitive being wrong, not the pages.
 *
 * Measured against the references: ChatGPT/Codex and SpreadJam settings both
 * run a toggle at roughly 1.4× their row title. Against Mora's 12px title that
 * is ~17px, which is `sm`, not `default`.
 *
 * `sm` remains right for dense surfaces too — the default shipped into the
 * campaigns table once and read as the loudest object on the page, louder than
 * the campaign name it belonged to.
 *
 * `default` is retained for any surface that genuinely runs a 14px+ label. If
 * you are reaching for it, name that label size first.
 */
export interface SwitchProps extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
  size?: 'default' | 'sm';
  /**
   * Shows real state that this surface cannot change — NOT `disabled`.
   *
   * `disabled` washes the track to 50% opacity, which is correct for a control
   * you are temporarily forbidden to use and wrong for one that is REPORTING
   * something. In the campaigns table it made a live, delivering, spending
   * campaign render in the same grey as a paused one, so the column answered
   * "can Mora write this?" when the reader was asking "is this running?".
   *
   * `readOnly` keeps the on-colour at full strength, keeps the row hoverable so
   * a tooltip can explain why it does not move, and refuses the toggle. Pair it
   * with a tooltip that says what the state is and why it is fixed — a control
   * that silently ignores a click is its own bug.
   */
  readOnly?: boolean;
}

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, size = 'default', readOnly = false, onCheckedChange, ...props }, ref) => (
    <SwitchPrimitives.Root
      className={cn(
        'peer inline-flex shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-foreground/80',
        // Use a subtle border for unchecked state for better dark mode contrast
        'data-[state=unchecked]:bg-muted data-[state=unchecked]:border data-[state=unchecked]:border-border',
        size === 'sm' ? 'h-4 w-7' : 'h-6 w-11',
        readOnly && 'cursor-default',
        className,
      )}
      aria-readonly={readOnly || undefined}
      onCheckedChange={readOnly ? undefined : onCheckedChange}
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb
        className={cn(
          'pointer-events-none block rounded-full bg-background ring-0 transition-transform data-[state=unchecked]:translate-x-0',
          // Add subtle shadow to thumb for better visibility
          'shadow-sm',
          size === 'sm'
            ? 'size-3 data-[state=checked]:translate-x-3.5'
            : 'size-5 data-[state=checked]:translate-x-5',
        )}
      />
    </SwitchPrimitives.Root>
  ),
);
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
